import { useCallback } from "react";
import { useAppDispatch } from "@store/store-helper";
import { useSearchParams } from "react-router-dom";
import { SetEnabledAction } from "@components/dev-features-dialog/features";
import { QueryParams } from "@router/route-params";

/** Function type for handling feature toggle state changes */
type FeatureToggleHandler = (isEnabled: boolean) => void;

/** Function type for creating feature toggle handlers */
type CreateToggleHandler = (props: { setEnabledAction: SetEnabledAction; queryParam: QueryParams }) => FeatureToggleHandler;

/** Function type for updating URL search parameters */
type UpdateSearchParams = (queryParam: string, isEnabled: boolean) => void;

interface UseFeatureToggleReturn {
  /** Function to create a toggle handler for a specific feature */
  createToggleHandler: CreateToggleHandler;
    
  /** Function to update URL search parameters */
  updateSearchParams: UpdateSearchParams;
}

/**
 * Custom hook for managing feature toggle state
 */
export function useFeatureToggle(): UseFeatureToggleReturn {
  const dispatch = useAppDispatch();
  const [searchParams, setSearchParams] = useSearchParams();

  /**
   * Updates URL search parameters when a feature is toggled
   */
  const updateSearchParams: UpdateSearchParams = useCallback(
    (queryParam: string, isEnabled: boolean): void => {
      const params = new URLSearchParams(searchParams);
      
      // Remove existing parameter if present (case-insensitive search)
      const existingParam = [...params.keys()].find(
        (key) => key.toLowerCase() === queryParam.toLowerCase()
      );

      if (existingParam) {
        params.delete(existingParam);
      }

      // Add parameter if enabling the feature
      if (isEnabled) {
        params.set(queryParam, "true");
      }

      // Update search params
      setSearchParams(params);
    },
    [searchParams, setSearchParams]
  );

  /**
   * Creates a toggle handler for a specific feature
   */
  const createToggleHandler: CreateToggleHandler = useCallback(
    ({ setEnabledAction, queryParam }: {
      setEnabledAction: SetEnabledAction;
      queryParam: string;
    }): FeatureToggleHandler => {
      return (isEnabled: boolean): void => {
        // Update Redux state
        dispatch(setEnabledAction(isEnabled));
        
        // Sync with URL
        updateSearchParams(queryParam, isEnabled);
      };
    },
    [dispatch, updateSearchParams]
  );

  return {
    createToggleHandler,
    updateSearchParams,
  };
}
